import { createReducer } from '@reduxjs/toolkit';
import without from 'lodash/without';
import {
    setPostCollection,
    setPostCollectionLoading,
    setPostReaction,
    setPostRecords,
    removePostFromBookmarkCollections,
    addPostToBookmarkCollections,
    resetPostCollection,
    removePostRecords,
    setPostCollectionReload,
    removeSavedCollectionFromPost,
    saveCollectionFromPost,
    setPostBoosting,
    addReportedPost,
} from './actions';
import type { PostDataModel } from './types';

const DEFAULT_COLLECTION_STATE = {
    identifiers: [],
    isLoading: false,
    reload: false,
};

export const COLLECTIONS_NAMESPACE_PREFIX = 'collection_';

type PostRecords = Record<string, PostDataModel>;

interface PostsState {
    records: PostRecords;
    details: Record<string, { isLoading: boolean }>;
    collections: Record<
        string,
        {
            identifiers: string[];
            isLoading: boolean;
            next?: string;
            reload: boolean;
        }
    >;
    reportedPosts: Record<string, boolean>;
}

export const postsReducer = createReducer<PostsState>(
    {
        records: {},
        details: {},
        collections: {},
        reportedPosts: {},
    },
    (builder) => {
        builder
            .addCase(setPostRecords, (draft, action) => {
                action.payload.forEach((postModel) => {
                    draft.records[postModel.id] = postModel;
                });
            })

            .addCase(setPostCollection, (draft, action) => {
                const { namespace, next, identifiers } = action.payload;

                draft.collections[namespace] = draft.collections[namespace] ?? {
                    ...DEFAULT_COLLECTION_STATE,
                };

                draft.collections[namespace].next = next;
                draft.collections[namespace].identifiers = [
                    ...draft.collections[namespace].identifiers,
                    ...identifiers,
                ];
            })

            .addCase(resetPostCollection, (draft, action) => {
                draft.collections[action.payload] = {
                    ...DEFAULT_COLLECTION_STATE,
                };
            })

            .addCase(setPostCollectionLoading, (draft, action) => {
                const { namespace, isLoading } = action.payload;

                draft.collections[namespace] = draft.collections[namespace] ?? {
                    ...DEFAULT_COLLECTION_STATE,
                };
                draft.collections[namespace].isLoading = isLoading;
            })

            .addCase(setPostReaction, (draft, action) => {
                const { id, reactionState } = action.payload;

                if (draft.records[id]) {
                    draft.records[id].reactionState = reactionState;
                    draft.records[id].likes += reactionState === 'like' ? 1 : -1;
                }
            })

            .addCase(removeSavedCollectionFromPost, (draft, action) => {
                const { postId, collectionId } = action.payload;

                if (postId) {
                    if (!draft.records[postId].collectionData) {
                        return;
                    }

                    draft.records[postId].collectionData!.isSaved = false;
                } else if (collectionId) {
                    const collectionData = draft.records[collectionId]?.collectionData;
                    if (collectionData?.id) {
                        collectionData.isSaved = false;
                    }
                }
            })

            .addCase(saveCollectionFromPost, (draft, action) => {
                const { postId, collectionId } = action.payload;

                if (postId) {
                    if (!draft.records[postId].collectionData) {
                        return;
                    }

                    draft.records[postId].collectionData!.isSaved = true;
                } else if (collectionId) {
                    const collectionData = draft.records[collectionId]?.collectionData;
                    if (collectionData?.id) {
                        collectionData.isSaved = true;
                    }
                }
            })

            .addCase(setPostCollectionReload, (draft, action) => {
                const { namespace, reload } = action.payload;

                draft.collections[namespace] = draft.collections[namespace] ?? {
                    ...DEFAULT_COLLECTION_STATE,
                };
                draft.collections[namespace].reload = reload;
            })

            .addCase(removePostRecords, (draft, action) => {
                const identifiers = action.payload;

                identifiers.forEach((identifier) => {
                    delete draft.records[identifier];

                    Object.keys(draft.collections).forEach((collectionKey) => {
                        draft.collections[collectionKey].identifiers = without(
                            draft.collections[collectionKey].identifiers,
                            identifier,
                        );
                    });
                });
            })

            .addCase(removePostFromBookmarkCollections, (draft, action) => {
                const postId = action.payload;
                const savedCollections = Object.keys(draft.collections).filter((namespace) =>
                    namespace.startsWith(COLLECTIONS_NAMESPACE_PREFIX),
                );

                savedCollections.forEach((collectionKey) => {
                    draft.collections[collectionKey].identifiers = draft.collections[
                        collectionKey
                    ].identifiers.filter((identifier) => identifier !== postId);
                });
            })

            .addCase(addPostToBookmarkCollections, ({ collections }, action) => {
                const { postId, namespace } = action.payload;

                collections[namespace] = collections[namespace] ?? {
                    ...DEFAULT_COLLECTION_STATE,
                };

                collections[namespace].identifiers = [
                    postId,
                    ...collections[namespace].identifiers,
                ];

                if (collections[namespace].next) {
                    // @ts-ignore
                    const next = parseInt(collections[namespace].next, 10) + 1;
                    collections[namespace].next = next.toString();
                }
            })

            .addCase(setPostBoosting, (draft, action) => {
                const { id, enabledDefaultBoosting } = action.payload;

                if (draft.records[id]) {
                    draft.records[id].enabledDefaultBoosting = enabledDefaultBoosting;
                }
            })

            .addCase(addReportedPost, (draft, action) => {
                draft.reportedPosts[action.payload] = true;
            });
    },
);
